import type { Filter, ExtendedVenueType } from '@cvent/venue-search-shared/types';
import type { VenueTypeOptions } from '@/utils/filters/options/getVenueTypeOptions';
import { useCallback, useState } from 'react';
import Icon from '@/components/shared/Icons';
import dynamic from 'next/dynamic';
import type { CheckboxGroupOption } from '@cvent/carina/components/Checkbox/CheckboxGroup/CheckboxGroup';
import { ExtendedVenueType as ExtendedVenueTypes } from '@/models/ExtendedVenueType';

const CheckboxGroup = dynamic(() => import('@/components/shared/checkbox/CheckboxGroup'), { ssr: false });
const TextLink = dynamic(() => import('@/components/shared/TextLink'), { ssr: false });

type Props = {
  options: VenueTypeOptions;
  selected: Filter;
  setSelected: (selections: Filter) => void;
  labels: {
    title: string;
    selectAll: string;
    clear: string;
  };
  showTitle: boolean;
  setShowShadow: (value: boolean) => void;
  testID?: string;
};

export default function VenueTypeFilter({
  options,
  selected,
  setSelected,
  labels,
  showTitle,
  setShowShadow,
  testID
}: Props): JSX.Element {
  const [category, setCategory] = useState<string>('');

  const onUpdate = useCallback(
    selections => {
      // Remove Restaurant Price Range Filter if Restaurant filter is not selected
      if (selected && !selections.includes(ExtendedVenueTypes.Values.RESTAURANT)) {
        const { PRICE_CATEGORY: _, ...newSelected } = selected;
        setSelected({ ...newSelected, VENUE_TYPE: selections });
      } else {
        setSelected({ ...selected, VENUE_TYPE: selections });
      }
    },
    [selected, setSelected]
  );

  const updateCategory = useCallback(
    (categoryKey: string) => {
      if (categoryKey === category) {
        setCategory('');
        setShowShadow(false);
      } else {
        setCategory(categoryKey);
        setShowShadow(true);
      }
    },
    [category, setShowShadow]
  );

  const getOptions = useCallback(
    (venueTypeOptions: { label: string; value: string }[]): CheckboxGroupOption[] =>
      venueTypeOptions.map(option => ({
        label: <div className="-mt-0.5 text-b-md/snug">{option.label}</div>,
        value: option.value
      })),
    []
  );

  const getOptionValues = useCallback(
    (venueTypeOptions: { value: ExtendedVenueType }[]): ExtendedVenueType[] =>
      venueTypeOptions.map(option => option.value),
    []
  );

  const onSelectAll = useCallback(
    (values: ExtendedVenueType[], categoryKey: string) => {
      const result = values.concat(selected.VENUE_TYPE?.filter(value => !values.includes(value)) || []);
      setSelected({ ...selected, VENUE_TYPE: result });
      setCategory(categoryKey);
      setShowShadow(true);
    },
    [selected, setSelected, setShowShadow]
  );

  const onClearAll = useCallback(
    (values: ExtendedVenueType[], categoryKey: string) => {
      setSelected({
        ...selected,
        VENUE_TYPE: selected.VENUE_TYPE?.filter(value => !values.includes(value))
      });
      setCategory(categoryKey);
      setShowShadow(true);
    },
    [selected, setSelected, setShowShadow]
  );

  const selectAllButton = useCallback(
    (values: ExtendedVenueType[], categoryKey: string) => {
      const disabled = values.every(r => selected.VENUE_TYPE?.includes(r));
      return (
        <div className="mr-4 text-b-xs/tight">
          <TextLink
            ariaLabel={labels.selectAll}
            className={`font-normal ${disabled ? 'text-neutral-40' : 'text-brand-60'}`}
            disabled={disabled}
            onClick={
              disabled
                ? undefined
                : e => {
                    e.stopPropagation();
                    onSelectAll(values, categoryKey);
                  }
            }
            onKeyUp={e => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                onSelectAll(values, categoryKey);
              }
            }}
            testID={`${testID}-${categoryKey}-select-all`}
          >
            {labels.selectAll}
          </TextLink>
        </div>
      );
    },
    [labels.selectAll, onSelectAll, selected.VENUE_TYPE, testID]
  );

  const clearButton = useCallback(
    (values: ExtendedVenueType[], categoryKey: string) => {
      const disabled = !selected.VENUE_TYPE?.some(r => values.includes(r));
      return (
        <div className="mr-2 text-b-xs/tight">
          <TextLink
            ariaLabel={labels.clear}
            className={`font-normal ${disabled ? 'text-neutral-40' : 'text-brand-60'}`}
            disabled={disabled}
            onClick={
              disabled
                ? undefined
                : e => {
                    e.stopPropagation();
                    onClearAll(values, categoryKey);
                  }
            }
            onKeyUp={e => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                onClearAll(values, categoryKey);
              }
            }}
            testID={`${testID}-${categoryKey}-clear`}
          >
            {labels.clear}
          </TextLink>
        </div>
      );
    },
    [labels.clear, onClearAll, selected.VENUE_TYPE, testID]
  );

  const child: JSX.Element = (
    <div className="relative">
      {options.map(option => {
        if (option.categoryName) {
          return (
            <div key={option.key}>
              <div
                role="button"
                tabIndex={0}
                data-cvent-id={`${option.key}-header`}
                className="flex h-9 items-center justify-between"
                onClick={() => updateCategory(option.key)}
                aria-label={option.categoryName}
                onKeyUp={event => {
                  if (event.key === 'Enter') {
                    updateCategory(option.key);
                  }
                }}
              >
                <div className="flex items-center">
                  <Icon
                    id={category === option.key ? 'chevron-up' : 'chevron-down'}
                    testID={
                      category === option.key
                        ? `${testID}-${option.categoryName}-chevron-up`
                        : `${testID}-${option.categoryName}-chevron-down`
                    }
                    className="mr-2 h-4 w-4"
                  />
                  <div className="text-b-md/snug">{option.categoryName}</div>
                </div>
                <div className="flex items-center">
                  {selectAllButton(getOptionValues(option.values), option.key)}
                  {clearButton(getOptionValues(option.values), option.key)}
                </div>
              </div>
              {category === option.key && (
                <div className="pl-6">
                  <CheckboxGroup
                    name={`${option.categoryName}-options`}
                    labelText=""
                    testID={`${testID}-${option.categoryName}-options`}
                    options={getOptions(option.values)}
                    selected={selected.VENUE_TYPE}
                    onUpdate={onUpdate}
                  />
                </div>
              )}
            </div>
          );
        }
        return (
          <div key="default-venue-types">
            <CheckboxGroup
              name="venue-types-other-options"
              labelText=""
              testID={`${testID}-other-options`}
              options={getOptions(option.values)}
              selected={selected.VENUE_TYPE}
              onUpdate={onUpdate}
            />
          </div>
        );
      })}
    </div>
  );

  return showTitle ? (
    <div className="border-y border-neutral-20 py-3">
      <div className="mb-2 text-b-xl/loose font-medium">{labels.title}</div>
      {child}
    </div>
  ) : (
    child
  );
}
